import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { showCurrency } from '../../utils';
import DebtDeletionModal from '../../components/DebtDeletionModal/DebtDeletionModal';
import { Section, StatStrip, DataTable, IdChip, RowActionsMenu, Button } from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';
import { useClientSort } from '../../hooks/useClientSort';
import { dateValue } from '../../utils/tableSort';

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

function ContractDebts({ contract, rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const { id } = useParams();
	const [deletingDebt, setDeletingDebt] = useState(null);

	const isClosed = contract?.status === 'closed';

	const { sortedRows, sortColumn, direction, onSort } = useClientSort(rows, {
		defaultColumn: 'id',
		accessors: {
			id: (debt) => debt.customId,
			date: (debt) => dateValue(debt.date),
			endDate: (debt) => dateValue(debt.endDate),
		},
	});

	const stats = useMemo(() => {
		const balanceByCurrency = new Map();
		const amountByCurrency = new Map();
		let nextDue = null;
		rows.forEach((debt) => {
			const currency = debt.currency || '—';
			balanceByCurrency.set(currency, (balanceByCurrency.get(currency) || 0) + (debt.balance || 0));
			amountByCurrency.set(currency, (amountByCurrency.get(currency) || 0) + (debt.amount || 0));
			if (debt.endDate && (!nextDue || moment.utc(debt.endDate).isBefore(moment.utc(nextDue.endDate)))) {
				nextDue = debt;
			}
		});
		return [
			{ label: t('entity.debt.plural'), value: rows.length },
			{
				label: t('field.dueAmount'),
				value: [...amountByCurrency.entries()].map(([currency, amount]) => formatMoney(currency, amount)).join(' · ') || '—',
				mono: true,
			},
			{
				label: t('field.previousBalance'),
				value: [...balanceByCurrency.entries()].map(([currency, amount]) => formatMoney(currency, amount)).join(' · ') || '—',
				mono: true,
				subTone: [...balanceByCurrency.values()].some((amount) => amount > 0) ? 'down' : 'neutral',
			},
			{
				label: t('field.endDate'),
				value: nextDue ? formatDate(nextDue.endDate) : '—',
				small: true,
				mono: true,
				sub: nextDue ? formatMoney(nextDue.currency, nextDue.amount) : undefined,
			},
		];
	}, [rows, t]);

	function newDebtHandler() {
		navigate(`/AdminScreen/contracts/contract/${id}/newDebt`);
	}

	function newPaymentHandler() {
		const returnTo = encodeURIComponent(`/AdminScreen/contracts/contract/${id}/cobros`);
		navigate(`/AdminScreen/contracts/contract/${id}/newContractPayment?returnTo=${returnTo}`);
	}

	function editDebtHandler(debt) {
		navigate(`/AdminScreen/contracts/contract/${id}/debts/${debt._id}/edit`);
	}

	const columns = [
		{ key: 'id', header: 'ID', sortable: true, render: (debt) => <IdChip>{debt.customId}</IdChip> },
		{ key: 'concept', header: t('entity.concept'), render: (debt) => debt.concept },
		{ key: 'date', header: t('field.date'), sortable: true, render: (debt) => formatDate(debt.date) },
		{ key: 'endDate', header: t('field.endDate'), sortable: true, render: (debt) => formatDate(debt.endDate) },
		{
			key: 'amount',
			header: t('field.dueAmount'),
			className: 'tw:text-right',
			render: (debt) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(debt.currency, debt.amount)}</span>,
		},
		{
			key: 'balance',
			header: t('field.balance'),
			className: 'tw:text-right',
			render: (debt) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(debt.currency, debt.balance)}</span>,
		},
	];

	const renderRowActions = (debt) => (
		<RowActionsMenu
			items={[
				{ label: t('action.edit'), onClick: () => editDebtHandler(debt) },
				{ label: t('action.delete'), onClick: () => setDeletingDebt(debt), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			{!isClosed && (
				<div className='tw:flex tw:justify-end tw:gap-2'>
					<Button size='sm' onClick={newDebtHandler}>
						{t('action.newDebt')}
					</Button>
					<Button size='sm' variant='primary' onClick={newPaymentHandler}>
						{t('action.addCollection')}
					</Button>
				</div>
			)}
			<Section title={t('title.collections')}>
				<DataTable
					columns={columns}
					rows={sortedRows}
					loading={loading}
					error={error}
					sortColumn={sortColumn}
					direction={direction}
					onSort={onSort}
					onRowClick={(debt) => navigate(`/AdminScreen/debts/debt/${debt._id}`)}
					rowActions={renderRowActions}
					emptyMessage={t('msg.noPendingDuesForContract')}
				/>
			</Section>

			<DebtDeletionModal
				show={Boolean(deletingDebt)}
				debt={deletingDebt}
				onClose={() => setDeletingDebt(null)}
				onDeleted={() => {
					setDeletingDebt(null);
					onRefetch();
				}}
			/>
		</div>
	);
}

export default ContractDebts;
